import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import type { OrganizationType, ModuleSelectionRequest, MigrationProperty } from '@/types';
import type { BridgeEntityInfo, MatchedModule, MatchedSpecialModule } from '@/lib/api';

export interface OrganizationData {
  organizationType: OrganizationType;
  amplifyOrganizationUuid?: string | undefined;
  organizationName: string;
  streetAddress?: string | undefined;
  addressLine2?: string | undefined;
  city?: string | undefined;
  state?: string | undefined;
  zipCode?: string | undefined;
  ownerFirstName?: string | undefined;
  ownerLastName?: string | undefined;
  ownerEmail?: string | undefined;
  ownerPhone?: string | undefined;
  billing?: string | undefined;
  sendInvite?: boolean | undefined;
  euid?: string | undefined;
}

interface WizardState {
  step: number;
  organizationType: OrganizationType | null;
  organizationData: OrganizationData | null;
  selectedModules: ModuleSelectionRequest[];
  isValid: boolean;
  // Bridge integration
  bridgeEntityUuid: string | undefined;
  bridgeEntity: BridgeEntityInfo | undefined;
  matchedModules: MatchedModule[];
  specialModules: MatchedSpecialModule[];
  // Per-migration property overrides (keyed by moduleKey)
  modulePropertyOverrides: Record<string, MigrationProperty[]>;
  // User migration toggle per module (keyed by moduleKey, default true)
  userMigrationEnabled: Record<string, boolean>;
}

interface WizardActions {
  setStep: (step: number) => void;
  nextStep: () => void;
  prevStep: () => void;
  setOrganizationType: (type: OrganizationType) => void;
  setOrganizationData: (data: OrganizationData) => void;
  setSelectedModules: (modules: ModuleSelectionRequest[]) => void;
  addModule: (module: ModuleSelectionRequest) => void;
  removeModule: (moduleKey: string) => void;
  setIsValid: (isValid: boolean) => void;
  reset: () => void;
  // Bridge integration
  setBridgeEntityUuid: (uuid: string) => void;
  setBridgeResult: (entity: BridgeEntityInfo, modules: MatchedModule[], specialModules: MatchedSpecialModule[]) => void;
  clearBridgeResult: () => void;
  // Module property overrides
  setModulePropertyOverrides: (moduleKey: string, properties: MigrationProperty[]) => void;
  setModulePropertyValue: (moduleKey: string, propertyKey: string, value: string | number | boolean) => void;
  clearModulePropertyOverrides: () => void;
  // User migration toggle
  setUserMigrationEnabled: (moduleKey: string, enabled: boolean) => void;
}

type WizardStore = WizardState & { actions: WizardActions };

const initialState: WizardState = {
  step: 1,
  organizationType: null,
  organizationData: null,
  selectedModules: [],
  isValid: false,
  // Bridge integration
  bridgeEntityUuid: undefined,
  bridgeEntity: undefined,
  matchedModules: [],
  specialModules: [],
  // Module property overrides
  modulePropertyOverrides: {},
  // User migration toggle
  userMigrationEnabled: {},
};

export const useWizardStore = create<WizardStore>()(
  devtools(
    persist(
      immer((set) => ({
        ...initialState,
        actions: {
          setStep: (step) =>
            set((state) => {
              state.step = step;
            }),
          nextStep: () =>
            set((state) => {
              state.step += 1;
            }),
          prevStep: () =>
            set((state) => {
              state.step = Math.max(1, state.step - 1);
            }),
          setOrganizationType: (type) =>
            set((state) => {
              state.organizationType = type;
            }),
          setOrganizationData: (data) =>
            set((state) => {
              state.organizationData = data;
            }),
          setSelectedModules: (modules) =>
            set((state) => {
              state.selectedModules = modules;
            }),
          addModule: (module) =>
            set((state) => {
              const exists = state.selectedModules.some(
                (m) => m.moduleKey === module.moduleKey
              );
              if (!exists) {
                state.selectedModules.push(module);
              }
            }),
          removeModule: (moduleKey) =>
            set((state) => {
              state.selectedModules = state.selectedModules.filter(
                (m) => m.moduleKey !== moduleKey
              );
              delete state.modulePropertyOverrides[moduleKey];
            }),
          setIsValid: (isValid) =>
            set((state) => {
              state.isValid = isValid;
            }),
          reset: () =>
            set((state) => {
              Object.assign(state, initialState);
            }),
          // Bridge integration
          setBridgeEntityUuid: (uuid) =>
            set((state) => {
              state.bridgeEntityUuid = uuid;
            }),
          setBridgeResult: (entity, modules, specialModules) =>
            set((state) => {
              state.bridgeEntity = entity;
              state.matchedModules = modules;
              state.specialModules = specialModules;
              // Auto-select all clean modules with full module data
              state.selectedModules = modules
                .filter((m) => m.duplicateCheck === 'clean')
                .map((m) => ({
                  moduleKey: m.moduleKey,
                  sourceId: m.labelId,
                  amplifyModuleId: m.amplifyModuleId ?? 0,
                  bridgeProductId: m.bridgeProductId,
                }));
              state.modulePropertyOverrides = {};
              // Default user migration to enabled for all modules that support it
              state.userMigrationEnabled = Object.fromEntries(
                modules
                  .filter((m) => m.userMigrationAvailable)
                  .map((m) => [m.moduleKey, true])
              );
            }),
          clearBridgeResult: () =>
            set((state) => {
              state.bridgeEntity = undefined;
              state.matchedModules = [];
              state.specialModules = [];
              state.selectedModules = [];
              state.modulePropertyOverrides = {};
              state.userMigrationEnabled = {};
            }),
          // Module property overrides
          setModulePropertyOverrides: (moduleKey, properties) =>
            set((state) => {
              state.modulePropertyOverrides[moduleKey] = properties;
            }),
          setModulePropertyValue: (moduleKey, propertyKey, value) =>
            set((state) => {
              const props = state.modulePropertyOverrides[moduleKey];
              if (!props) return;
              const prop = props.find((p) => p.key === propertyKey);
              if (prop) {
                prop.value = value;
              }
            }),
          clearModulePropertyOverrides: () =>
            set((state) => {
              state.modulePropertyOverrides = {};
            }),
          setUserMigrationEnabled: (moduleKey, enabled) =>
            set((state) => {
              state.userMigrationEnabled[moduleKey] = enabled;
            }),
        },
      })),
      {
        name: 'wizard-storage',
        partialize: (state) => ({
          step: state.step,
          organizationType: state.organizationType,
          organizationData: state.organizationData,
          selectedModules: state.selectedModules,
          // Bridge integration
          bridgeEntityUuid: state.bridgeEntityUuid,
          bridgeEntity: state.bridgeEntity,
          matchedModules: state.matchedModules,
          specialModules: state.specialModules,
          // Module property overrides
          modulePropertyOverrides: state.modulePropertyOverrides,
          // User migration toggle
          userMigrationEnabled: state.userMigrationEnabled,
        }),
      }
    ),
    { name: 'WizardStore' }
  )
);

// Selectors for convenient access
export const useWizardStep = () => useWizardStore((state) => state.step);
export const useWizardOrgType = () => useWizardStore((state) => state.organizationType);
export const useWizardOrgData = () => useWizardStore((state) => state.organizationData);
export const useWizardModules = () => useWizardStore((state) => state.selectedModules);
export const useWizardIsValid = () => useWizardStore((state) => state.isValid);
export const useWizardActions = () => useWizardStore((state) => state.actions);
// Bridge integration selectors
export const useWizardBridgeUuid = () => useWizardStore((state) => state.bridgeEntityUuid);
export const useWizardBridgeEntity = () => useWizardStore((state) => state.bridgeEntity);
export const useWizardMatchedModules = () => useWizardStore((state) => state.matchedModules);
export const useWizardSpecialModules = () => useWizardStore((state) => state.specialModules);
// Module property overrides selector
export const useWizardModulePropertyOverrides = () =>
  useWizardStore((state) => state.modulePropertyOverrides);
// User migration toggle selector
export const useWizardUserMigrationEnabled = () =>
  useWizardStore((state) => state.userMigrationEnabled);
